import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

export interface AiWorkComic {
  id: number;
  work_id: number;
  user_id: number;
  content_url?: string;
  visibility?: string;
  metadata?: Record<string, unknown>;
  generation_started_at?: string;
  generation_completed_at?: string;
  failed_at?: string;
  failure_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface AiProductFeedback {
  id: number;
  rating?: number;
  comment?: string;
  completed_at?: number;
}

export interface AiProductFeedbackResponse {
  success: boolean;
  ai_product_feedback: AiProductFeedback;
}

export interface GetComicResponse {
  success: boolean;
  ai_work_comic: AiWorkComic;
  error?: string;
}

export interface CreateComicResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// Fetch comic for a specific work
export const getWorkComic = async (
  workId: number,
): Promise<GetComicResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_comic_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch comic');
  }

  return response.json();
};

// Create/generate comic for a specific work
export const createWorkComic = async (
  workId: number,
): Promise<CreateComicResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_comic_create_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        work_id: workId,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create comic');
  }

  return response.json();
};

// Clear/delete comic for a specific work
export const clearWorkComic = async (
  workId: number,
): Promise<{ success: boolean }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_comic_destroy_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'DELETE',
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to clear comic');
  }

  return response.json();
};

// Update comic visibility
export const updateWorkComic = async (
  workId: number,
  visibility: 'public' | 'private',
): Promise<GetComicResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_comic_update_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        visibility,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to update comic');
  }

  return response.json();
};

// Reuse existing AI product feedback functions from analogies
export const getOrCreateAiProductFeedback = async (
  workId: number,
  productType: string,
): Promise<AiProductFeedbackResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_product_feedback_get_or_create_product_feedback_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        work_id: workId,
        product_type: productType,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to get or create AI product feedback');
  }

  return response.json();
};

export const updateAiProductFeedback = async (params: {
  id: number;
  rating?: number;
  comment?: string;
  completed_at?: number;
}): Promise<void> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_product_feedback_update_product_feedback_path({
      subdomain_param: 'api',
      id: params.id,
    }),
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to update AI product feedback');
  }
};

// Upload comic image to create Photo record
export const uploadComicImage = async (
  workId: number,
): Promise<{ success: boolean; photo: { id: string; url: string } }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_comic_upload_image_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to upload comic image');
  }

  return response.json();
};
